import { Text, type TextProps } from '@mantine/core';
import type { ReactNode } from 'react';

/** Props for {@link Eyebrow}. */
export interface EyebrowProps extends Omit<TextProps, 'children'> {
  children: ReactNode;
}

/**
 * Eyebrow — the MTB eyebrow: JetBrains Mono, uppercase, 700, wide tracking,
 * 11px. The one label treatment that sits above a page title, a stat figure
 * or a section, everywhere in the vendor app.
 *
 * Shell: any. Purely presentational; no store or network access.
 *
 * Non-obvious contracts:
 *   - Font family and tracking come from the design-system tokens
 *     (`--eyebrow-font` = JetBrains Mono, `--tracking-eyebrow` = 0.18em),
 *     not from hand-authored values. The `vendor-cases` design template's
 *     own `Label` helper hard-codes `.14em`; the token file is the canonical
 *     source (`mtb-design-system/platforms/tokens.json`), so the token wins
 *     and the divergence is reported rather than mirrored.
 *   - 11px is the design system's hard floor for anything a dealer reads
 *     (`notes.md` §9.4). Do not add a smaller size variant.
 */
export function Eyebrow({ children, ...rest }: EyebrowProps) {
  return (
    <Text
      fz={11}
      fw={700}
      tt="uppercase"
      c="dimmed"
      {...rest}
      style={{
        fontFamily: 'var(--eyebrow-font)',
        letterSpacing: 'var(--tracking-eyebrow)',
        ...(rest.style ?? {}),
      }}
    >
      {children}
    </Text>
  );
}
